/**
 * Copyright (c) 2024 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Sebastian Hartmann <s.hartmann@techdivision.com>
 */

import React, { useRef, useState } from 'react';
import { Box, Button, Skeleton } from '@mui/material';
import GoogleMapReact from 'google-map-react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Marker } from './Components/Marker/Marker';
import { StudioSearchField } from './Components/Overlay/Searchfield/StudioSearchField';
import { SelectedStudio } from './Components/Overlay/SelectedStudio/SelectedStudio';
import './styles.scss';
import { useStudioLocator } from './useStudioLocator';
import StudioAutoComplete from './Components/StudioAutoComplete';
import { useStudioUrls } from './useStudioUrls';

/**
 * StudioLocatorMap Component
 * @returns {Element}
 * @constructor
 */
export default function StudioLocator(props) {
  const { apiKey, params } = props;

  const {
    defaultLatitude,
    defaultLongitude,
    defaultInitialZoom,
    defaultZoom,
    defaultMapHeight,
    displayMode,
    countryCode,
    studioId,
  } = params;

  const {
    isLoading,
    getMappedStudioData,
    currentSelectedMarker,
    setCurrentSelectedMarker,
    setSelectedMarker,
    toggleMobileStudioDetails,
    mobileDetailsVisible,
    studioSpecificLongitude,
    studioSpecificLatitude,
  } = useStudioLocator(countryCode, studioId);

  const { studioUrls } = useStudioUrls();

  const mapRef = useRef(null);
  const mapsRef = useRef(null);
  const [apiLoaded, setApiLoaded] = useState(false);
  const { t } = useTranslation();

  const mapConfiguration = {
    center: {
      lat: studioSpecificLatitude || Number(defaultLatitude),
      lng: studioSpecificLongitude || Number(defaultLongitude),
    },
    initialZoom: parseInt(defaultInitialZoom, 10),
    zoom: parseInt(defaultZoom, 10),
  };

  /**
     * Handle the initialization of the maps api
     * @param map
     * @param maps
     */
  const handleApiLoaded = (map, maps) => {
    mapRef.current = map;
    mapsRef.current = maps;
    setApiLoaded(true);

    // After map is loaded zoom in closer
    const timer = setTimeout(() => {
      mapRef.current.setZoom(mapConfiguration.zoom);
    }, 1000);
    return () => clearTimeout(timer);
  };

  const isWithAutoComplete = displayMode === 'studioSelector';

  /**
     * Render react wrapper for Google Maps
     * @returns {JSX.Element}
     */
  const renderStudioLocator = () => {
    // While loading return the loading overlay and block the space for the map with a skeleton component
    if (isLoading) {
      return <Skeleton animation="wave" variant="rectangular" width="100%" height={defaultMapHeight} />;
    }

    return (
      <>
        {isWithAutoComplete ? (
          <Box>
            {apiLoaded ? (
              <StudioAutoComplete
                studios={getMappedStudioData()}
                maps={mapsRef.current}
                map={mapRef.current}
                setSelectedMarker={setSelectedMarker}
                studioUrls={studioUrls}
              />
            ) : null}
          </Box>
        ) : null}

        <Box className="map-container" sx={{ width: '100%', height: defaultMapHeight }}>
          <Box className="map" sx={{ width: '100%', height: '100%' }}>
            <GoogleMapReact
              bootstrapURLKeys={{
                key: apiKey,
                libraries: ['geometry', 'places'],
              }}
              yesIWantToUseGoogleMapApiInternals
              onGoogleApiLoaded={({ map, maps }) => handleApiLoaded(map, maps)}
              defaultCenter={mapConfiguration.center}
              defaultZoom={mapConfiguration.initialZoom}
            >
              {getMappedStudioData().map((studio) => (
                <Marker
                  key={studio.id}
                  onClick={() => setSelectedMarker(studio)}
                  alt={studio.name}
                  lat={studio.address.latitude}
                  lng={studio.address.longitude}
                />
              ))}
            </GoogleMapReact>
          </Box>
          {apiLoaded && !isWithAutoComplete && !currentSelectedMarker ? (
            <StudioSearchField maps={mapsRef.current} map={mapRef.current} />
          ) : null}
          <Box className={currentSelectedMarker ? 'overlay visible' : 'overlay'}>
            {currentSelectedMarker ? (
              <SelectedStudio
                studio={currentSelectedMarker}
                setCurrentSelectedMarker={setCurrentSelectedMarker}
                studioUrls={studioUrls}
                className={mobileDetailsVisible ? 'visible' : ''}
              />
            ) : null}
          </Box>
        </Box>
        <Button
          onClick={toggleMobileStudioDetails}
          className={currentSelectedMarker ? 'mobile-list-toggle visible' : 'mobile-list-toggle'}
          variant="contained"
        >
          {mobileDetailsVisible ? t('showMap') : t('showDetails')}
        </Button>
      </>
    );
  };

  return <>{renderStudioLocator()}</>;
}

StudioLocator.propTypes = {
  apiKey: PropTypes.string.isRequired,
};
